import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CurrencyExchange as ExchangeIcon
} from '@mui/icons-material';
import { tenantTokenService } from '../../../services/skillcoinService';

const TokenExchangeSettings = ({ token: initialToken, onUpdate }) => {
  const [internalToken, setInternalToken] = useState(initialToken);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allowExternalExchange, setAllowExternalExchange] = useState(false);
  const [allowPeerTransfer, setAllowPeerTransfer] = useState(true);
  const [automaticApproval, setAutomaticApproval] = useState(false);
  const [platformFee, setPlatformFee] = useState(5.0);
  const [tenantFee, setTenantFee] = useState(2.0);
  const [minExchange, setMinExchange] = useState(0.01);
  const [maxExchangePerDay, setMaxExchangePerDay] = useState(1000);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [openCurrencyModal, setOpenCurrencyModal] = useState(false);
  const [currentCurrency, setCurrentCurrency] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (initialToken) {
      // Update internal token state when props change
      setInternalToken(initialToken);
      
      // Load the exchange settings from the token
      const settings = initialToken.exchangeSettings || {};
      
      setAllowExternalExchange(settings.allowExternalExchange || false);
      setAllowPeerTransfer(settings.allowPeerTransfer || true);
      setAutomaticApproval(settings.automaticApproval || false);
      setPlatformFee(settings.platformFee || 5.0);
      setTenantFee(settings.tenantFee || 2.0);
      setMinExchange(settings.minExchange || 0.01);
      setMaxExchangePerDay(settings.maxExchangePerDay || 1000);
      setExchangeRates(settings.exchangeRates || []);
      
      setLoading(false);
    }
  }, [initialToken]);

  // Add the loadTokenExchangeSettings function to handle refresh button clicks
  const loadTokenExchangeSettings = () => {
    if (initialToken) {
      // Load the exchange settings from the token
      const settings = initialToken.exchangeSettings || {};
      
      setAllowExternalExchange(settings.allowExternalExchange || false);
      setAllowPeerTransfer(settings.allowPeerTransfer || true);
      setAutomaticApproval(settings.automaticApproval || false);
      setPlatformFee(settings.platformFee || 5.0);
      setTenantFee(settings.tenantFee || 2.0);
      setMinExchange(settings.minExchange || 0.01);
      setMaxExchangePerDay(settings.maxExchangePerDay || 1000);
      setExchangeRates(settings.exchangeRates || []);
      
      setSuccess('Settings refreshed');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const saveExchangeSettings = async () => {
    setSaving(true);
    try {
      const exchangeSettings = {
        allowExternalExchange,
        allowPeerTransfer,
        automaticApproval,
        platformFee,
        tenantFee,
        minExchange,
        maxExchangePerDay,
        exchangeRates
      };
      
      // Properly structure the data with 'settings' property as expected by the API
      await tenantTokenService.updateTokenSettings({ 
        exchangeSettings: exchangeSettings 
      });
      
      // Update the local token object with the new settings to prevent them from being lost
      if (internalToken) {
        const updatedToken = { ...internalToken };
        updatedToken.exchangeSettings = exchangeSettings;
        
        // If using the API directly worked, we should update our local state too
        setInternalToken(updatedToken);
      }
      
      setSuccess('Exchange settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
      // Call the parent's onUpdate with the updated token if it exists
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate();
      }
    } catch (err) {
      console.error('Error saving exchange settings:', err);
      setError('Failed to save exchange settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCurrency = () => {
    setCurrentCurrency({
      currency: '',
      name: '',
      symbol: '',
      rate: 1.0,
      isDefault: false
    });
    setIsEditing(false);
    setOpenCurrencyModal(true);
  };

  const handleEditCurrency = (currency) => {
    setCurrentCurrency(currency);
    setIsEditing(true);
    setOpenCurrencyModal(true);
  };

  const handleDeleteCurrency = (currencyCode) => {
    setExchangeRates((prevRates) => prevRates.filter((rate) => rate.currency !== currencyCode));
  };

  const handleSetDefaultCurrency = (currencyCode) => {
    setExchangeRates((prevRates) =>
      prevRates.map((rate) => ({
        ...rate,
        isDefault: rate.currency === currencyCode
      }))
    );
  };

  const handleCurrencyFieldChange = (field, value) => {
    setCurrentCurrency((prevCurrency) => ({
      ...prevCurrency,
      [field]: value
    }));
  };

  const handleSetDefaultToggle = (event) => {
    setCurrentCurrency((prevCurrency) => ({
      ...prevCurrency,
      isDefault: event.target.checked
    }));
  };

  const handleSaveCurrency = () => {
    if (isEditing) {
      setExchangeRates((prevRates) =>
        prevRates.map((rate) =>
          rate.currency === currentCurrency.currency ? currentCurrency : rate
        )
      );
    } else {
      setExchangeRates((prevRates) => [...prevRates, currentCurrency]);
    }
    setOpenCurrencyModal(false);
  };

  const getCurrencySelectOptions = () => {
    return [
      { code: 'USD', name: 'US Dollar', symbol: '$' },
      { code: 'EUR', name: 'Euro', symbol: '€' },
      { code: 'GBP', name: 'British Pound', symbol: '£' },
      { code: 'JPY', name: 'Japanese Yen', symbol: '¥' }
    ];
  };

  const selectPredefinedCurrency = (currencyCode) => {
    const selectedCurrency = getCurrencySelectOptions().find(
      (currency) => currency.code === currencyCode
    );
    if (selectedCurrency) {
      setCurrentCurrency({
        ...currentCurrency,
        currency: selectedCurrency.code,
        name: selectedCurrency.name,
        symbol: selectedCurrency.symbol
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
          <ExchangeIcon sx={{ mr: 1 }} />
          Token Exchange Settings
        </Typography>
        <Box>
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={loadTokenExchangeSettings} 
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Exchange Features
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowExternalExchange}
                    onChange={(e) => setAllowExternalExchange(e.target.checked)}
                  />
                }
                label="Allow Token to Currency Exchange"
              />
              <Tooltip title="Enable this to allow members to exchange tokens for real-world currencies">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={allowPeerTransfer}
                    onChange={(e) => setAllowPeerTransfer(e.target.checked)}
                  />
                }
                label="Allow Peer-to-Peer Token Transfers"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={automaticApproval}
                    onChange={(e) => setAutomaticApproval(e.target.checked)}
                  />
                }
                label="Automatic Exchange Approval"
              />
              <Tooltip title="When enabled, exchanges are processed automatically. Otherwise, admin approval is required.">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Fee & Limit Settings
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Platform Fee (%)"
                    fullWidth
                    type="number"
                    value={platformFee}
                    onChange={(e) => setPlatformFee(parseFloat(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { min: 0, max: 100, step: 0.1 }
                    }}
                    helperText="Fee charged by the platform on exchanges"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Tenant Fee (%)"
                    fullWidth
                    type="number"
                    value={tenantFee}
                    onChange={(e) => setTenantFee(parseFloat(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { min: 0, max: 100, step: 0.1 }
                    }}
                    helperText="Additional fee for your organization"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Minimum Exchange Amount"
                    fullWidth
                    type="number"
                    value={minExchange}
                    onChange={(e) => setMinExchange(parseFloat(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Tokens</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Max Exchange Per Day"
                    fullWidth
                    type="number"
                    value={maxExchangePerDay}
                    onChange={(e) => setMaxExchangePerDay(parseFloat(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Tokens</InputAdornment>,
                      inputProps: { min: 0, step: 1 }
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Currency Exchange Rates
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Define the exchange rates between your token and different currencies.
              The default currency will be used as the primary exchange currency.
            </Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Default</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Rate (1 Token =)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exchangeRates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No currencies defined</TableCell>
                  </TableRow>
                ) : (
                  exchangeRates.map((rate) => (
                    <TableRow key={rate.currency}>
                      <TableCell>
                        <Radio
                          checked={rate.isDefault}
                          onChange={() => handleSetDefaultCurrency(rate.currency)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${rate.currency} - ${rate.name}`} 
                          size="small"
                          color={rate.isDefault ? "primary" : "default"}
                          variant={rate.isDefault ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>{rate.symbol}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          1 {rate.symbol} = {
                            rate && rate.rate !== undefined ?
                              (typeof rate.rate === 'number' ? rate.rate.toFixed(4) : parseFloat(rate.rate || 0).toFixed(4))
                              : '0.0000'
                          } {rate.currency}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditCurrency(rate)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteCurrency(rate.currency)}
                          color="error"
                          disabled={rate.isDefault}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddCurrency}
              variant="outlined"
            >
              Add Currency
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={saveExchangeSettings}
          disabled={saving}
          sx={{ minWidth: 150 }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save Settings'}
        </Button>
      </Box>

      {/* Currency Add/Edit Modal */}
      <Dialog
        open={openCurrencyModal}
        onClose={() => setOpenCurrencyModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Edit Currency' : 'Add New Currency'}
        </DialogTitle>
        <DialogContent dividers>
          <Box p={1}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Currency</InputLabel>
              <Select
                value={currentCurrency?.currency || ''}
                onChange={(e) => selectPredefinedCurrency(e.target.value)}
                label="Select Currency"
              >
                <MenuItem value="" disabled>
                  <em>Select a predefined currency</em>
                </MenuItem>
                {getCurrencySelectOptions().map((currency) => (
                  <MenuItem key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name} ({currency.symbol})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Currency Code"
              fullWidth
              margin="dense"
              value={currentCurrency?.currency || ''}
              onChange={(e) => handleCurrencyFieldChange('currency', e.target.value)}
              helperText="ISO currency code (e.g. USD, EUR, GBP)"
              required
              disabled={isEditing}
            />

            <TextField
              label="Currency Name"
              fullWidth
              margin="dense"
              value={currentCurrency?.name || ''}
              onChange={(e) => handleCurrencyFieldChange('name', e.target.value)}
              helperText="Full name of the currency"
              required
            />

            <TextField
              label="Currency Symbol"
              fullWidth
              margin="dense"
              value={currentCurrency?.symbol || ''}
              onChange={(e) => handleCurrencyFieldChange('symbol', e.target.value)}
              helperText="Symbol used to represent the currency"
              required
            />

            <TextField
              label="Exchange Rate"
              fullWidth
              margin="dense"
              type="number"
              value={currentCurrency?.rate || 1.0}
              onChange={(e) => handleCurrencyFieldChange('rate', e.target.value)}
              helperText="Value of 1 token in this currency"
              InputProps={{
                startAdornment: <InputAdornment position="start">1 Token =</InputAdornment>,
                endAdornment: <InputAdornment position="end">{currentCurrency?.currency}</InputAdornment>,
                inputProps: { min: 0.0001, step: 0.0001 }
              }}
              required
            />

            <FormControlLabel
              control={
                <Switch
                  checked={currentCurrency?.isDefault || false}
                  onChange={handleSetDefaultToggle}
                  disabled={isEditing && currentCurrency?.isDefault}
                />
              }
              label="Set as default currency"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCurrencyModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCurrency} 
            variant="contained" 
            color="primary"
            disabled={
              !currentCurrency?.currency || 
              !currentCurrency?.name || 
              !currentCurrency?.symbol ||
              !currentCurrency?.rate
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TokenExchangeSettings;